// v1 → SaveV2 migration. Reads the legacy keys (never deletes them — v1
// stays functional during staging) and folds variant-district bests
// (prenzlauer2-4, zehlendorf2-4) into their parent districts, keeping max.

import { defaultSave, type SaveV2, type HintEntry } from './schema';

export const LEGACY_KEYS = {
  progress: 'karateJump_progress', // v1 js/persistence.js
  highScore: 'karateJump_highScore', // v1 js/game3d.js:32
  hints: 'karateJump_hints', // v1 js/hint-manager.js
} as const;

/** v1 variant keys → v2 parent district ids. */
export function foldDistrictKey(key: string): string {
  const match = /^(prenzlauer|zehlendorf)\d+$/.exec(key);
  return match ? (match[1] as string) : key;
}

interface LegacyProgress {
  bestDistance?: unknown;
  bestBeltIndex?: unknown;
  districtBest?: unknown;
  musicMuted?: unknown;
}

export interface LegacyStorage {
  getItem(key: string): string | null;
}

export function migrateFromV1(storage: LegacyStorage): SaveV2 {
  const save = defaultSave();

  const rawHighScore = storage.getItem(LEGACY_KEYS.highScore);
  if (rawHighScore !== null) {
    const value = Number(rawHighScore);
    if (Number.isFinite(value) && value > 0) save.highScore = value;
  }

  const rawProgress = storage.getItem(LEGACY_KEYS.progress);
  if (rawProgress !== null) {
    try {
      const progress = JSON.parse(rawProgress) as LegacyProgress;
      if (typeof progress.bestDistance === 'number' && progress.bestDistance > 0) {
        save.bests.distance = progress.bestDistance;
      }
      if (typeof progress.bestBeltIndex === 'number' && progress.bestBeltIndex > 0) {
        save.bests.beltIndex = Math.min(Math.floor(progress.bestBeltIndex), 8);
      }
      if (typeof progress.districtBest === 'object' && progress.districtBest !== null) {
        for (const [key, value] of Object.entries(progress.districtBest)) {
          if (typeof value !== 'number' || !Number.isFinite(value)) continue;
          const folded = foldDistrictKey(key);
          save.bests.byDistrict[folded] = Math.max(save.bests.byDistrict[folded] ?? 0, value);
        }
      }
      if (progress.musicMuted === true) save.settings.musicMuted = true;
    } catch {
      // Corrupt progress JSON — keep defaults
    }
  }

  const rawHints = storage.getItem(LEGACY_KEYS.hints);
  if (rawHints !== null) {
    try {
      const hints = JSON.parse(rawHints) as Record<string, unknown>;
      for (const [id, entry] of Object.entries(hints)) {
        if (typeof entry === 'object' && entry !== null) {
          const e = entry as Partial<HintEntry>;
          save.settings.hintsSeen[id] = {
            shown: typeof e.shown === 'number' ? e.shown : 0,
            done: e.done === true,
          };
        }
      }
    } catch {
      // Corrupt hints JSON — keep defaults
    }
  }

  return save;
}
